import { useCallback, useState } from 'react'
import { Grid, H, P, Wrapper } from '@farewill/ui'

import { Task, TaskAttributes } from 'lib/models/task'
import { TaskStage } from 'lib/models/task-stages'

import TasksStage from 'components/tasks-panel/tasks-stage'
import { useTaskStagesContext } from 'components/tasks-panel/context'
import { TaskStageFilterPanel } from './task-stage-filter-panel'
import { noop } from 'lib/functions/noop'

type ProbateTaskStagesListProps = {
  probateCaseId: number
  taskStages: TaskStage[]
}

const ProbateTaskStagesList = ({
  probateCaseId,
  taskStages,
}: ProbateTaskStagesListProps) => {
  const { fetchTaskStages } = useTaskStagesContext()
  const refetchTasks = useCallback(() => fetchTaskStages(), [fetchTaskStages])
  /** Default to select the first in progress stage, or fallback to the sale stage.
   * Although there should always be at least one in progress stage, since the next
   * stage startedAt is populated when the previous stage is marked as completed,
   * older cases could be missing the startedAt field on the first stage (sale stage)
   * since they would have been created before we began setting startedAt upon
   * creation of the probate case.
   **/
  const firstInProgressStage = taskStages?.find(
    ({ attributes: { status } }) => status === 'In progress'
  )

  const saleStage = taskStages?.find(
    ({ attributes: { slug } }) => slug === 'sale'
  )

  const [selectedTaskStageId, setSelectedTaskStageId] = useState<number>(
    (firstInProgressStage || (saleStage as TaskStage)).id
  )

  const selectedTaskStage = taskStages.find(
    ({ id }) => id === selectedTaskStageId
  ) as TaskStage

  const getFormattedTasks = (tasks: TaskAttributes[]): Task[] => {
    //Sorting by time created does not work as they have the same created time so it leads to akward ordering
    return tasks
      .sort((a, b) => a.id - b.id)
      .map((task) => {
        return {
          attributes: task,
          id: task.id,
        }
      })
  }

  return (
    <Grid>
      <Grid.Item spanFromM={4} spanFromXL={3}>
        <TaskStageFilterPanel
          probateCaseId={probateCaseId}
          taskStages={taskStages}
          selectedTaskStageId={selectedTaskStageId}
          setSelectedTaskStageId={setSelectedTaskStageId}
        />
      </Grid.Item>
      <Grid.Item spanFromM={8} spanFromXL={9}>
        <Wrapper>
          <Wrapper>
            <H size="S"> {selectedTaskStage.attributes.label} </H>
            <P>
              {
                selectedTaskStage.attributes.tasks.filter(
                  (task) => task.coreTask && task.completedAt
                ).length
              }{' '}
              of{' '}
              {
                selectedTaskStage.attributes.tasks.filter(
                  (task) => task.coreTask
                ).length
              }
            </P>
            <TasksStage
              productId={{ probateCaseId }}
              tasks={getFormattedTasks(selectedTaskStage.attributes.tasks)}
              enableCommenting
              enableHelpAssignment
              key={selectedTaskStage.attributes.slug}
              stage={selectedTaskStage.attributes.slug}
              stageId={selectedTaskStage.id}
              isPanelExpanded
              setPanelExpanded={noop}
              isStageExpanded
              refetchTasks={refetchTasks}
              refetchTasksOnComplete
            />
          </Wrapper>
        </Wrapper>
      </Grid.Item>
    </Grid>
  )
}

export default ProbateTaskStagesList
